/* eslint-disable no-param-reassign */
import { createAsyncThunk, createSlice, PayloadAction } from '@reduxjs/toolkit';

import { filterType } from '../../config/enum';
import { CommonFilterProperties, FilterUpdateProperties } from '../../interface/filter.interface';
import {
  ClearFilters,
  RemoveSingleFilterFromList,
  UpdateCommonFilter,
  ConfigureListFilterResponse,
} from '../../shared-functions/CommonFilters';
import { removeEmptyKeys } from '../../shared-functions/DateTime';
import deviceHubApi from '../../utils/api/device-hub';

/**
 * Fetch all global device models from the API
 */
export const fetchAllGlobalModels = createAsyncThunk(
  'globalModel/fetchAllGlobalModels',
  async (query: any, { dispatch, rejectWithValue }) => {
    try {
      const response = await deviceHubApi.fetchGlobalModels(removeEmptyKeys(query));
      return response.data;
    } catch (error: any) {
      dispatch({
        type: 'notification/setNotificationMessage',
        payload: {
          type: 'error',
          message: error?.response?.data?.message || 'Failed to fetch global models',
          status: true,
        },
      });
      return rejectWithValue(error.response?.data);
    }
  },
);

/**
 * Fetch global model filter options
 */
export const fetchGlobalModelFilters = createAsyncThunk<CommonFilterProperties[], any>(
  'globalModel/fetchGlobalModelFilters',
  async (query, { dispatch }) => {
    try {
      const response = await deviceHubApi.modelFilter(query);
      const updatedResponse: CommonFilterProperties[] = ConfigureListFilterResponse(response?.data);
      return updatedResponse;
    } catch (error: any) {
      dispatch({
        type: 'notification/setNotificationMessage',
        payload: {
          type: 'error',
          message: error?.response?.data?.message || 'Something went wrong!',
          code: error?.response?.data?.code || 200,
          status: true,
        },
      });
      throw error;
    }
  },
);

const availableFilters: CommonFilterProperties[] = [
  {
    name: 'Search here...',
    key: 'searchBy',
    type: filterType.TEXT,
    value: '',
    applyFilter: false,
    enable: true,
    searchBy: true,
  },
];

/**
 * Initial state for Global Model slice
 */
const initialState = {
  models: [] as any[],
  loading: false,
  totalResults: 0,
  totalPages: 0,
  page: 1,
  limit: 10,
  filters: availableFilters,
  isFilterUpdated: false,
  filterListLoading: false,
  sortBy: null,
  sortField: null,
};

const globalModelSlice = createSlice({
  name: 'globalModel',
  initialState,
  reducers: {
    /**
     * Set current page
     * @param {any} state - The state
     * @param {any} action - The action
     */
    setGlobalModelPage: (state, action) => {
      state.page = action.payload;
    },
    /**
     * Set limit
     * @param {any} state - The state
     * @param {any} action - The action
     */
    setGlobalModelLimit: (state, action) => {
      state.limit = action.payload;
    },
    /**
     * Update filters
     * @param {any} state - The current state.
     * @param {any} action - The action object.
     */
    updateGlobalModelFilters: (state, action: PayloadAction<FilterUpdateProperties>) => {
      const payload = { ...action.payload };
      if (payload.type === 'add') {
        const { updatedCommonFilter, isFilterUpdated } = UpdateCommonFilter(
          state.filters,
          action.payload,
        );
        state.filters = updatedCommonFilter;
        state.isFilterUpdated = isFilterUpdated;
      } else if (payload.type === 'remove') {
        state.filters = RemoveSingleFilterFromList(state.filters, payload.key);
        state.isFilterUpdated = true;
      } else if (payload.type === 'clear') {
        state.filters = ClearFilters(state.filters);
        state.isFilterUpdated = true;
        state.sortBy = null;
        state.sortField = null;
      }
    },
    /**
     * Disable filter applied flag
     * @param {any} state - The state
     */
    disableGlobalModelFilterApplied: (state) => {
      state.isFilterUpdated = false;
    },
    /**
     * Set sort by
     * @param {any} state - The state
     * @param {any} action - The action
     */
    setGlobalModelSortBy: (state, action) => {
      state.sortBy = action.payload;
    },
    /**
     * Set sort field
     * @param {any} state - The state
     * @param {any} action - The action
     */
    setGlobalModelSortField: (state, action) => {
      state.sortField = action.payload;
    },
  },
  /**
   * Handle asynchronous thunk actions
   * @param {any} builder - The builder
   */
  extraReducers: (builder) => {
    builder
      .addCase(fetchAllGlobalModels.pending, (state) => {
        state.loading = true;
      })
      .addCase(fetchAllGlobalModels.fulfilled, (state, action) => {
        state.loading = false;
        state.models = action.payload.results || action.payload || [];
        state.totalResults =
          action.payload.totalResults ||
          (action.payload.results ? action.payload.results.length : 0);
        state.totalPages = action.payload.totalPages || 1;
        state.page = action.payload.page || state.page;
        state.limit = action.payload.limit || state.limit;
      })
      .addCase(fetchAllGlobalModels.rejected, (state) => {
        state.loading = false;
      })
      .addCase(fetchGlobalModelFilters.pending, (state) => {
        state.filterListLoading = true;
      })
      .addCase(
        fetchGlobalModelFilters.fulfilled,
        (state, action: PayloadAction<CommonFilterProperties[]>) => {
          state.filterListLoading = false;
          state.filters = [...(state.filters || []), ...action.payload];
        },
      )
      .addCase(fetchGlobalModelFilters.rejected, (state) => {
        state.filterListLoading = false;
      });
  },
});

export const {
  setGlobalModelPage,
  setGlobalModelLimit,
  updateGlobalModelFilters,
  disableGlobalModelFilterApplied,
  setGlobalModelSortBy,
  setGlobalModelSortField,
} = globalModelSlice.actions;

export default globalModelSlice.reducer;
